import { ProductType } from '@qred/shared-enums';
import posthog from 'posthog-js';
import { ClientType, EAnalyticsEvent, FlowTypes, StepStatus } from '~/enums';
import {
  IAnalyticEventParams,
  IGtmAnalyticEventParams,
  IStepName,
} from '~/Interfaces';
import type { ProductTypeLowercase } from '~/schemas/DTO';
import { AppDispatch, GetStateFunc } from '..';
import { selectStepConfig as selectCardOfferStepConfig } from '../selectors/cardOffer.selector';
import {
  pushToGtmHelper,
  shouldTrackTimeSpentOnStep,
} from './analyticsActions.utils';

interface IBaseEventProps {
  errorMessage?: string;
  stepActionNumber?: number;
  extraContext?: {};
}

interface IActionEventProps {
  actionName: string;
  stepStatus?: never;
}

interface IStepStatusEventProps {
  actionName?: never;
  stepStatus: StepStatus;
}

interface IBaseStandaloneEventProps {
  eventName: Exclude<
    EAnalyticsEvent,
    | EAnalyticsEvent.OnboardingApplicationFlow
    | EAnalyticsEvent.OnboardingOfferFlow
  >;
  errorMessage?: string;
  stepNumber?: number;
  stepName?: keyof IStepName;
  product: ProductType;
  extraContext?: {};
}

export type StandaloneAnalyticsActionsProps = IBaseStandaloneEventProps &
  (IActionEventProps | IStepStatusEventProps);

export type AnalyticsActionsProps = IBaseEventProps &
  (IActionEventProps | IStepStatusEventProps);

export const trackCardApplicationActions = ({
  actionName,
  stepStatus,
  errorMessage,
  extraContext,
}: AnalyticsActionsProps) => (
  _dispatch: AppDispatch,
  getState: GetStateFunc
) => {
  const state = getState();

  const {
    cardApplication: {
      clientId,
      form: {
        applicationUuid,
        applicant: { email, phone },
      },
      currentStepNumber,
      currentStepName,
      stepStartTimestamp,
    },
    intl: { market, language },
  } = state;

  const timeSpentOnStep = shouldTrackTimeSpentOnStep(actionName)
    ? Math.ceil(
        (new Date().getTime() - new Date(stepStartTimestamp).getTime()) / 1000
      )
    : undefined;

  const baseEventProperties: IAnalyticEventParams = {
    eventName: EAnalyticsEvent.OnboardingApplicationFlow,
    product: ProductType.CARD.toLowerCase() as ProductTypeLowercase,
    stepNumber: currentStepNumber,
    ...(clientId && { userId: clientId }),
    orderId: applicationUuid,
    stepName: currentStepName,
    clientType: ClientType.Unknown,
    market,
    language,
    errorMessage,
    actionName,
    stepStatus,
    timeSpentOnStep,
    timestamp: new Date().toISOString(),
    ...extraContext,
  };

  const gtmEventProperties: IGtmAnalyticEventParams = {
    ...baseEventProperties,
    phoneNumber: phone,
    email,
  };

  if (window.Cypress) {
    window.posthogCapture(
      EAnalyticsEvent.OnboardingApplicationFlow,
      baseEventProperties
    );
  }

  posthog.capture(
    EAnalyticsEvent.OnboardingApplicationFlow,
    baseEventProperties
  );
  pushToGtmHelper(
    EAnalyticsEvent.OnboardingApplicationFlow,
    gtmEventProperties
  );
};

export const trackCardOfferActions = ({
  stepStatus,
  actionName,
  errorMessage,
  extraContext,
}: AnalyticsActionsProps) => (
  _dispatch: AppDispatch,
  getState: GetStateFunc
) => {
  const state = getState();
  const {
    cardOffer: {
      currentStep,
      overview: {
        clientId,
        applicant: { email, phoneNumber },
      },
      stepStartTimestamp,
    },
    intl: { market, language },
  } = state;
  const cardOfferStepConfig = selectCardOfferStepConfig(state);
  const currentStepConfig = cardOfferStepConfig[currentStep - 1];
  const stepName = currentStepConfig?.name;
  const timeSpentOnStep = shouldTrackTimeSpentOnStep(actionName)
    ? Math.ceil(
        (new Date().getTime() - new Date(stepStartTimestamp).getTime()) / 1000
      )
    : undefined;

  const baseEventProperties: IAnalyticEventParams = {
    eventName: EAnalyticsEvent.OnboardingOfferFlow,
    product: ProductType.CARD.toLowerCase() as ProductTypeLowercase,
    stepNumber: currentStep,
    stepName,
    clientType: ClientType.Unknown,
    ...(clientId && { userId: clientId }),
    market,
    language,
    errorMessage,
    actionName,
    stepStatus,
    flowType: FlowTypes.Authenticated,
    timeSpentOnStep,
    timestamp: new Date().toISOString(),
    ...extraContext,
  };

  const gtmEventProperties = {
    ...baseEventProperties,
    phoneNumber,
    email,
  };

  if (window.Cypress) {
    window.posthogCapture(
      EAnalyticsEvent.OnboardingOfferFlow,
      baseEventProperties
    );
  }

  posthog.capture(EAnalyticsEvent.OnboardingOfferFlow, baseEventProperties);
  pushToGtmHelper(EAnalyticsEvent.OnboardingOfferFlow, gtmEventProperties);
};

export const trackStandaloneActions = ({
  eventName,
  stepName,
  stepNumber,
  stepStatus,
  actionName,
  errorMessage,
  product,
  extraContext,
}: StandaloneAnalyticsActionsProps) => (
  _dispatch: AppDispatch,
  getState: GetStateFunc
) => {
  const state = getState();
  const {
    intl: { market, language },
  } = state;

  const baseEventProperties: IAnalyticEventParams = {
    eventName,
    stepName,
    stepNumber,
    stepStatus,
    actionName,
    errorMessage,
    product: product.toLowerCase() as ProductTypeLowercase,
    market,
    language,
    timestamp: new Date().toISOString(),
    ...extraContext,
  };

  if (window.Cypress) {
    window.posthogCapture(eventName, baseEventProperties);
  }

  posthog.capture(eventName, baseEventProperties);
};
